import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  ActivationEnd,
  NavigationEnd,
  NavigationStart,
  Router,
  UrlSegment,
} from '@angular/router';
import { BreadcrumbToken, LocalizedValueModel } from '@osos/shared/types';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { buffer, filter, map, mergeMap, tap } from 'rxjs/operators';

const ROUTE_DATA_BREADCRUMB = 'breadcrumb';
interface BreadCrumbRouteModel {
  text: string | LocalizedValueModel;
  isStatic?: boolean;
  endSegmentIndex?: number;
  preserveQueryParams?: boolean;
}

type BreadCrumbRoute = string | BreadCrumbRouteModel;
interface BreadcrumbItem {
  label: string | LocalizedValueModel;
  url: string;
  queryParams?: any;
}

interface StackedBreadcrumbToken {
  token: BreadcrumbToken;
  value: string | LocalizedValueModel;
}

@Injectable()
export class BreadcrumbService {
  breadcrumbs$: Observable<BreadcrumbItem[]>;

  private _breadcrumbsSub$ = new BehaviorSubject<BreadcrumbItem[]>([]);

  private _stackedTokens: StackedBreadcrumbToken[] = [];
  private _breadcrumbsPending = false;

  constructor(private router: Router) {
    this.breadcrumbs$ = this._breadcrumbsSub$.asObservable();

    const routerEvents$ = this.router.events.pipe(
      filter(
        (event) =>
          event instanceof NavigationStart ||
          event instanceof NavigationEnd ||
          event instanceof ActivationEnd,
      ),
    );

    routerEvents$
      .pipe(
        tap((event) => {
          if (event instanceof NavigationStart) {
            this._breadcrumbsPending = true;
          }
        }),
        filter((event) => event instanceof ActivationEnd),
        map((event) => (event as ActivationEnd).snapshot),
        buffer(
          routerEvents$.pipe(filter((event) => event instanceof NavigationEnd)),
        ),
        mergeMap((routes) => this._prepareBreadcrumbs(routes.reverse())),
        tap(() => (this._breadcrumbsPending = false)),
      )
      .subscribe((breadcrumbs) => {
        this._breadcrumbsSub$.next(this._handleStackedTokens(breadcrumbs));
      });
  }

  refreshBreadcrumbs() {
    this._breadcrumbsSub$.next([...this._breadcrumbsSub$.value]);
  }

  set(token: BreadcrumbToken, value: string | LocalizedValueModel) {
    // If breadcrumbs still pending, stack the tokens and resolve them later once the current navigation is ended.
    if (this._breadcrumbsPending) {
      this._stackedTokens.push({ token, value });
      return;
    }

    const _currentBreadcrubmbs = this._breadcrumbsSub$.value;

    if (!_currentBreadcrubmbs.some((item) => item.label === token)) {
      return;
    }

    const _newBreadcurmbs = _currentBreadcrubmbs.map((item) => {
      if (item.label === token) {
        item.label = value;
      }
      return item;
    });

    this._breadcrumbsSub$.next(_newBreadcurmbs);
  }

  private _prepareBreadcrumbs(
    routes: ActivatedRouteSnapshot[],
  ): Observable<BreadcrumbItem[]> {
    const breadcrumbs: BreadcrumbItem[] = [];
    let url = '';
    for (const snapshot of routes) {
      if (!snapshot.routeConfig?.path && !snapshot.routeConfig?.matcher) {
        continue;
      }

      const tempUrl = url;
      url += `/${this.buildUrl(snapshot.url)}`;
      const breadcrumb = snapshot.data[ROUTE_DATA_BREADCRUMB] as
        | BreadCrumbRoute
        | BreadCrumbRoute[];

      const crumbs = !Array.isArray(breadcrumb) ? [breadcrumb] : breadcrumb;
      let endIndex = 1;
      crumbs
        .filter(Boolean)
        .map((crumb) => {
          return typeof crumb === 'string'
            ? {
                text: crumb,
                endSegmentIndex: endIndex++,
              }
            : crumb;
        })
        .forEach((b) => {
          const item = this.buildCrumb(b, snapshot, tempUrl);
          breadcrumbs.push(item);
        });
    }

    return of(breadcrumbs);
  }

  buildCrumb(
    crumb: BreadCrumbRouteModel,
    snapshot: ActivatedRouteSnapshot,
    baseUrl: string,
  ): BreadcrumbItem {
    if (crumb.isStatic) {
      return {
        label: crumb.text,
        url: null,
      };
    }

    const segments = snapshot.url;
    const url = this.buildUrl(segments.slice(0, crumb.endSegmentIndex));
    let queryParams = {};
    if (crumb.preserveQueryParams) {
      queryParams = snapshot.queryParams;
    }

    return {
      label: crumb.text,
      url: `${baseUrl}/${url}`,
      queryParams,
    };
  }
  buildUrl(segments: UrlSegment[]): string {
    return segments.map((s) => s.path).join('/');
  }

  private _handleStackedTokens(
    breadcrumbs: BreadcrumbItem[],
  ): BreadcrumbItem[] {
    if (!this._stackedTokens.length) return breadcrumbs;

    this._stackedTokens.forEach((tokenItem) => {
      const _matchedBreadcrumbs = breadcrumbs.filter(
        (item) => item.label === tokenItem.token,
      );

      if (_matchedBreadcrumbs.length) {
        _matchedBreadcrumbs.forEach((item) => (item.label = tokenItem.value));
      }
    });

    // Clear stacked tokens:
    this._stackedTokens = [];

    return [...breadcrumbs];
  }
}
